import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { getDragonAnnotation } from '../../utils/dragonAnnotations';
import { Spinner } from '../Spinner';
import { ErrorMessage } from '../ErrorMessage';
import { MobileDossierRoster } from './MobileDossierRoster';
import { MobileDossierDetailView } from './MobileDossierDetailView';
import { computeDossierTotals } from '../../utils/dossierTotals';
import { RESOURCES_SORT_OPTIONS, MILITARY_SORT_OPTIONS, sortDossierRows } from '../../utils/dossierSort';
import { matchesEffectFilter } from '../../utils/dossierEffectFilter';
import type { MobileDossierShellProps, TabId } from '../../pages/Dossier';

const TABS: { id: TabId; label: string }[] = [
  { id: 'military', label: 'Military' },
  { id: 'resources', label: 'Resources' },
  { id: 'effects', label: 'Effects' },
];

/**
 * SF-156 Component 2: mobile Dossier content — kingdom header card (tab-aware totals row), tab
 * strip, sort/filter row, roster list. Pure prop-consumer for `activeTab`/`mobileSortValue`/
 * `mobileEffectFilter`/`?member=` (Application Interface's State ownership section) — the shell
 * itself owns no state beyond ephemeral UI concerns (the focus-restore ref below).
 *
 * Owns loading/error/empty render-state — simplified to 3 mutually exclusive branches (see
 * Application Interface's Loading/error/empty ownership): `error` (co-rendered with any stale
 * `dossier` already loaded), `!error && dossier === null` (loading — covers both a real fetch in
 * progress and the page's `kingdomId === null` transient state identically), and
 * `!error && dossier !== null` (real content, regardless of `isLoading` — a background refresh
 * never blanks existing content).
 */
export function MobileDossierShell({
  dossier,
  currentTick,
  isLoading,
  error,
  refresh,
  activeTab,
  onTabChange,
  mobileSortValue,
  onSortChange,
  mobileEffectFilter,
  onEffectFilterChange,
  effectFilterOptions,
  selectedMember,
  onSelectMember,
  showSiblingChrome,
}: MobileDossierShellProps) {
  // Focus-restore on `?member=` deselect (Phase 3 — adopts MobileTargetFinderShell.tsx:49-64's
  // shell-owned pattern verbatim). Fires on any transition of `selectedMember` to `null`,
  // including hardware Back popping the query param.
  const lastSelectedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (selectedMember) {
      lastSelectedIdRef.current = selectedMember.provinceId;
      return;
    }
    const lastId = lastSelectedIdRef.current;
    if (lastId) {
      lastSelectedIdRef.current = null;
      const row = document.querySelector<HTMLElement>(`[data-member-id="${lastId}"]`);
      row?.focus();
    }
  }, [selectedMember]);

  // Effect-filter-change announcement — a second, mobile-only aria-live region, distinct from the
  // page's own kingdom-switch announcer (avoids clobbering / a no-repeat-on-same-count silent miss).
  const [filterAnnouncement, setFilterAnnouncement] = useState('');
  useEffect(() => {
    if (activeTab !== 'effects' || !dossier) return;
    const shown = dossier.provinces.filter((p) => matchesEffectFilter(p, mobileEffectFilter)).length;
    setFilterAnnouncement(`${shown} of ${dossier.provinces.length} provinces shown`);
  }, [mobileEffectFilter, activeTab, dossier]);

  const visibleRows = useMemo(() => {
    if (!dossier) return [];
    if (activeTab === 'effects') {
      return dossier.provinces.filter((p) => matchesEffectFilter(p, mobileEffectFilter));
    }
    return sortDossierRows(dossier.provinces, activeTab, mobileSortValue);
  }, [dossier, activeTab, mobileEffectFilter, mobileSortValue]);

  return (
    <div style={{ padding: 12 }}>
      <div aria-live="polite" className="sr-only">{filterAnnouncement}</div>

      {error && (
        <div style={{ marginBottom: 12 }}>
          <ErrorMessage message={error} />
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <button
              type="button"
              onClick={() => refresh()}
              style={{ minHeight: 44, padding: '0 16px', borderRadius: 6, background: COLORS.btnPrimary, color: COLORS.text.primary, border: 'none', cursor: 'pointer' }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {!error && dossier === null && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Spinner />
        </div>
      )}

      {dossier !== null && (
        selectedMember ? (
          <MobileDossierDetailView
            member={selectedMember}
            currentTick={currentTick}
            onBack={() => onSelectMember(null)}
            showSiblingChrome={showSiblingChrome}
          />
        ) : dossier.provinces.length === 0 ? (
          <div style={{ padding: 32, color: COLORS.text.muted, textAlign: 'center' }}>
            No members found for this kingdom.
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={() => refresh()}
                style={{ minHeight: 44, padding: '0 16px', borderRadius: 6, background: COLORS.btnPrimary, color: COLORS.text.primary, border: 'none', cursor: 'pointer' }}
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <>
            <KingdomHeaderCard dossier={dossier} activeTab={activeTab} onRefresh={refresh} isLoading={isLoading} />
            <TabStrip activeTab={activeTab} onTabChange={onTabChange} />
            <div
              role="tabpanel"
              id={`mobile-dossier-panel-${activeTab}`}
              aria-labelledby={`mobile-dossier-tab-${activeTab}`}
            >
              {activeTab === 'effects' ? (
                <EffectFilterRow
                  mobileEffectFilter={mobileEffectFilter}
                  effectFilterOptions={effectFilterOptions}
                  onEffectFilterChange={onEffectFilterChange}
                />
              ) : (
                <SortRow activeTab={activeTab} mobileSortValue={mobileSortValue} onSortChange={onSortChange} />
              )}
              {visibleRows.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: COLORS.text.muted, fontSize: 13 }}>
                  No provinces match this filter.
                </div>
              ) : (
                <MobileDossierRoster
                  rows={visibleRows}
                  activeTab={activeTab}
                  currentTick={currentTick}
                  onSelectMember={onSelectMember}
                />
              )}
            </div>
          </>
        )
      )}
    </div>
  );
}

function KingdomHeaderCard({
  dossier,
  activeTab,
  onRefresh,
  isLoading,
}: {
  dossier: NonNullable<MobileDossierShellProps['dossier']>;
  activeTab: TabId;
  onRefresh: () => void;
  isLoading: boolean;
}) {
  const dragon = getDragonAnnotation(dossier.dragonType);
  const totals = computeDossierTotals(activeTab, dossier.provinces);

  return (
    <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        <span style={{ color: COLORS.text.primary, fontSize: 17, fontWeight: 700 }}>
          {dossier.kingdomName ?? dossier.kingdomId}
          <span style={{ color: COLORS.text.tertiary, fontSize: 13, fontWeight: 500, marginLeft: 6 }}>({dossier.kingdomId})</span>
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {dossier.dragonType && dossier.dragonType !== 'NONE' && (
            <span
              style={{ fontSize: 10, padding: '2px 7px', borderRadius: 3, border: `1px solid ${COLORS.warning}`, color: COLORS.warning, textTransform: 'uppercase' }}
              title={dragon.economicEffect}
            >
              {dragon.label}
            </span>
          )}
          <button
            type="button"
            aria-label="Refresh dossier"
            onClick={onRefresh}
            disabled={isLoading}
            style={{ minHeight: 44, minWidth: 44, background: 'none', border: 'none', color: COLORS.link, cursor: isLoading ? 'wait' : 'pointer', fontSize: 16 }}
          >
            ⟳
          </button>
        </div>
      </div>

      <div data-testid="dossier-header-stats" style={{ display: 'flex', gap: 16, fontSize: 12, color: COLORS.text.secondary, flexWrap: 'wrap', marginBottom: 8 }}>
        {dossier.kingdomNetworth > 0 && (
          <span data-testid="dossier-header-nw"><span style={{ color: COLORS.text.muted }}>NW </span><span style={{ color: COLORS.gold }}>{formatNumber(dossier.kingdomNetworth)}</span></span>
        )}
        {dossier.kingdomLand > 0 && (
          <span data-testid="dossier-header-land"><span style={{ color: COLORS.text.muted }}>Land </span>{formatNumber(dossier.kingdomLand)}</span>
        )}
        <span data-testid="dossier-header-members"><span style={{ color: COLORS.text.muted }}>Members </span>{dossier.provinces.length}</span>
      </div>

      {dossier.atWarWith.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {dossier.atWarWith.map((id) => (
            <Link
              key={id}
              to={`/world/dossier/${id}`}
              style={{ fontSize: 11, padding: '2px 8px', border: `1px solid ${COLORS.negative}`, color: COLORS.negative, borderRadius: 3, textDecoration: 'none' }}
            >
              ⚔ {id}
            </Link>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${COLORS.border}` }}>
        {totals.cells.map((c) => (
          <div key={c.label} style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 9.5, color: COLORS.text.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{c.label}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: c.color }}>{c.value}</div>
          </div>
        ))}
        <div style={{ gridColumn: '1 / -1', fontSize: 10.5, color: COLORS.text.muted, marginTop: 2 }}>{totals.caveat}</div>
      </div>
    </div>
  );
}

function TabStrip({ activeTab, onTabChange }: { activeTab: TabId; onTabChange: (tab: TabId) => void }) {
  return (
    <div role="tablist" style={{ display: 'flex', borderBottom: `1px solid ${COLORS.border}`, marginBottom: 10 }}>
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          id={`mobile-dossier-tab-${t.id}`}
          aria-selected={activeTab === t.id}
          aria-controls={`mobile-dossier-panel-${t.id}`}
          onClick={() => onTabChange(t.id)}
          style={{
            flex: 1,
            background: 'none',
            border: 'none',
            padding: '10px 4px',
            fontSize: 12.5,
            fontWeight: 600,
            color: activeTab === t.id ? COLORS.text.primary : COLORS.text.muted,
            borderBottom: activeTab === t.id ? `2px solid ${COLORS.gold}` : '2px solid transparent',
            cursor: 'pointer',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function SortRow({
  activeTab,
  mobileSortValue,
  onSortChange,
}: {
  activeTab: TabId;
  mobileSortValue: MobileDossierShellProps['mobileSortValue'];
  onSortChange: MobileDossierShellProps['onSortChange'];
}) {
  const options = activeTab === 'military' ? MILITARY_SORT_OPTIONS : RESOURCES_SORT_OPTIONS;
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 12, color: COLORS.text.tertiary }}>
      Sort by
      <select
        value={mobileSortValue}
        onChange={(e) => onSortChange(e.target.value as MobileDossierShellProps['mobileSortValue'])}
        style={{ background: COLORS.inputBg, border: `1px solid ${COLORS.inputBorder}`, color: COLORS.text.primary, fontSize: 13, padding: '6px 8px', borderRadius: 5, minHeight: 44, flex: 1 }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function EffectFilterRow({
  mobileEffectFilter,
  effectFilterOptions,
  onEffectFilterChange,
}: {
  mobileEffectFilter: MobileDossierShellProps['mobileEffectFilter'];
  effectFilterOptions: MobileDossierShellProps['effectFilterOptions'];
  onEffectFilterChange: MobileDossierShellProps['onEffectFilterChange'];
}) {
  const categoryOptions = effectFilterOptions.filter((o) => o.group === 'category');
  const specificOptions = effectFilterOptions.filter((o) => o.group === 'specific');

  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 12, color: COLORS.text.tertiary }}>
      Show provinces with…
      <select
        value={mobileEffectFilter ?? ''}
        onChange={(e) => onEffectFilterChange(e.target.value === '' ? null : e.target.value)}
        style={{ background: COLORS.inputBg, border: `1px solid ${COLORS.inputBorder}`, color: COLORS.text.primary, fontSize: 13, padding: '6px 8px', borderRadius: 5, minHeight: 44, flex: 1 }}
      >
        <option value="">Any effect</option>
        <optgroup label="By category">
          {categoryOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </optgroup>
        {specificOptions.length > 0 && (
          <optgroup label="Specific effect">
            {specificOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </optgroup>
        )}
      </select>
    </label>
  );
}
